import { BaseApi } from "api/BaseApi.ts";
import { ApiResponse } from "types/models.ts";
import { sortSignedUrlParams } from "utils/sortSignedUrlParams.ts";

import { VerifyEmailLinkRequestWithEmail, VerifyEmailLinkRequestWithId } from "./hooks";

// import VerifyEmailRequest = App.Http.Requests.User.VerifyEmailRequest;
import GetUserResponse = App.Http.Responses.User.GetUserResponse;
import GetUsersResponse = App.Http.Responses.User.GetUsersResponse;
import CheckEmailRequest = App.Http.Requests.User.CheckEmailRequest;
import CheckEmailResponse = App.Http.Responses.User.CheckEmailResponse;
import ConfirmRegistrationRequest = App.Http.Requests.User.ConfirmRegistrationRequest;
import ConfirmRegistrationResendRequest = App.Http.Requests.User.ConfirmRegistrationResendRequest;
import RegistrationRequest = App.Http.Requests.User.RegistrationRequest;
import VerifyEmailResendRequest = App.Http.Requests.User.VerifyEmailResendRequest;
import ForgotPasswordRequest = App.Http.Requests.User.ForgotPasswordRequest;
import ResetPasswordRequest = App.Http.Requests.User.ResetPasswordRequest;
// import GetUserInvitationsRequest = App.Http.Requests.User.GetUserInvitationsRequest;
import GetTenantsResponse = App.Http.Responses.Tenant.GetTenantsResponse;
import VerifyEmailLinkRequest = App.Http.Requests.User.VerifyEmailLinkRequest;
import GetUsersRequest = App.Http.Requests.User.GetUsersRequest;
import GetUserConversionsApiTokenResponse = App.Http.Responses.User.GetUserConversionsApiTokenResponse;
import GetUserTenantPermissionsResponse = App.Http.Responses.User.GetUserTenantPermissionsResponse;
import UpdateUserRequest = App.Http.Requests.User.UpdateUserRequest;
import UpdateMeRequest = App.Http.Requests.User.UpdateMeRequest;
import ChangePasswordRequest = App.Http.Requests.User.ChangePasswordRequest;

class UsersApi extends BaseApi {
    /**
     * Display a listing of the resource.
     *
     * - Request: GetUsersRequest
     * - Response: GetUsersResponse
     */
    async getList(request: GetUsersRequest): Promise<GetUsersResponse> {
        return await this.get<GetUsersResponse, GetUsersRequest>("/users", {
            params: request,
        });
    }

    /**
     * Register user
     *
     * - Request: RegistrationRequest
     * - Response: GetUserResponse
     */
    async register(request: RegistrationRequest): Promise<GetUserResponse> {
        return await this.post<GetUserResponse, RegistrationRequest>("/users", request, { silentError: true });
    }

    /**
     * Check whether an email is already registered.
     *
     * Without tenant_id: plain uniqueness check (200 available, 422 taken).
     * With tenant_id + role: returns a CheckEmailResponse status.
     *
     * - Request: CheckEmailRequest
     * - Response: CheckEmailResponse when tenant_id is present, otherwise null
     */
    async checkEmail(request: CheckEmailRequest): Promise<CheckEmailResponse | null> {
        return await this.post<CheckEmailResponse | null, CheckEmailRequest>("/users/check-email", request, {
            silentError: true,
        });
    }

    /**
     * Resend the confirm-registration email (expired/invalid signed link).
     *
     * - Request: ConfirmRegistrationResendRequest
     * - Response: just 200
     */
    async confirmRegistrationResend(request: ConfirmRegistrationResendRequest): Promise<null> {
        return await this.post<null, ConfirmRegistrationResendRequest>("/users/confirm-registration/resend", request);
    }

    /**
     * Confirm a tenant join for an existing verified user (signed link).
     *
     * - Request: ConfirmRegistrationRequest
     * - Response: just 200
     */
    async confirmRegistration(request: ConfirmRegistrationRequest): Promise<null> {
        // The BE envelope for this endpoint omits "data" entirely, so the response resolves as
        // undefined — TanStack Query v5 throws "Query data cannot be undefined" for that and treats
        // the query as errored, so a successful confirmation must still resolve with null explicitly.
        await this.get<null, ConfirmRegistrationRequest>("/users/confirm-registration", {
            params: sortSignedUrlParams(request),
            silentError: true,
        });
        return null;
    }

    /**
     * Get specific user
     *
     * - Request: -
     * - Response: GetUserResponse
     */
    async getUser(userId: string): Promise<GetUserResponse> {
        return await this.get<GetUserResponse, void>(`/users/${userId}`);
    }

    /**
     * Get current authenticated user data.
     *
     * - Request: /
     * - Response: GetUserResponse
     */
    async getMe(request: GetUsersRequest): Promise<GetUserResponse> {
        return await this.get<GetUserResponse, GetUsersRequest>("/users/me", {
            params: request,
        });
    }

    /**
     * Get the current user's permissions in one active tenant.
     */
    async getMeTenantPermissions(tenantSlug: string, silentError = false): Promise<GetUserTenantPermissionsResponse> {
        return await this.get<GetUserTenantPermissionsResponse, void>(
            `/users/me/tenants/${encodeURIComponent(tenantSlug)}/permissions`,
            silentError ? { silentError } : undefined
        );
    }

    /**
     * Resend email verification.
     *
     * - Request: VerifyEmailResendRequest
     * - Response: just 200
     */
    async verifyEmailResend(request: VerifyEmailResendRequest): Promise<null> {
        return await this.post<null, VerifyEmailResendRequest>("/users/verify-email/resend", request);
    }

    /**
     * Verify email link validity
     *
     * - Request: VerifyEmailLinkRequest
     * - Response: 200 (GET) or GetUserResponse (POST)
     */
    async verifyEmailLink(request: VerifyEmailLinkRequestWithId): Promise<ApiResponse<null>> {
        return await this.getRaw<null, VerifyEmailLinkRequest>(`/users/verify-email/${request.id}/${request.hash}`, {
            // tenant_id/role must be forwarded exactly as issued — they're part of the signed URL's
            // signature, so omitting or changing them invalidates the link.
            params: sortSignedUrlParams({
                expires: request.expires,
                signature: request.signature,
                ...(request.tenant_id ? { tenant_id: request.tenant_id } : {}),
                ...(request.role ? { role: request.role } : {}),
            }),
        });
    }

    /**
     * Verify email address and set password.
     *
     * - Request: VerifyEmailRequest
     * - Response: 200 (GET) or GetUserResponse (POST)
     */
    async verifyEmail(request: VerifyEmailLinkRequestWithEmail): Promise<GetUserResponse> {
        return this.post<GetUserResponse, VerifyEmailLinkRequestWithEmail>(
            `/users/verify-email/${request.id}/${request.hash}`,
            {
                ...request,
            },
            {
                // tenant_id/role must be forwarded exactly as issued — they're part of the signed URL's
                // signature, so omitting or changing them invalidates the link.
                params: sortSignedUrlParams({
                    expires: request.expires,
                    signature: request.signature,
                    ...(request.tenant_id ? { tenant_id: request.tenant_id } : {}),
                    ...(request.role ? { role: request.role } : {}),
                }),
            }
        );
    }

    /**
     * Send email for password reset.
     *
     * - Request: ForgotPasswordRequest
     * - Response: JsonResponse
     */
    async forgotPassword(request: ForgotPasswordRequest): Promise<null> {
        return await this.post<null, ForgotPasswordRequest>("/users/forgot-password", request);
    }

    /**
     * Reset password.
     *
     * - Request: ResetPasswordRequest
     * - Response: just 200, 400
     */
    async resetPassword(request: ResetPasswordRequest): Promise<null> {
        return await this.post<null, ResetPasswordRequest>("/users/reset-password", request);
    }

    /**
     * Get user's tenants
     *
     * - Request: -
     * - Response: GetTenantsResponse
     */
    async getUserTenants(userId: string): Promise<GetTenantsResponse> {
        return await this.get<GetTenantsResponse, void>(`/users/${userId}/tenants`);
    }

    /**
     * Get current authenticated user conversions api token.
     *
     * - Request: /
     * - Response: GetUserConversionsApiTokenResponse
     */
    async getConversionApiToken(): Promise<GetUserConversionsApiTokenResponse> {
        return await this.get<GetUserConversionsApiTokenResponse, undefined>("/users/conversions-api-token");
    }

    /**
     * Regenerate conversions api token for current authenticated user.
     *
     * - Request: /
     * - Response: GetUserConversionsApiTokenResponse
     */
    async regenerateConversionApiToken(): Promise<GetUserConversionsApiTokenResponse> {
        return await this.post<GetUserConversionsApiTokenResponse, undefined>(
            "/users/conversions-api-token/regenerate"
        );
    }

    /**
     * Update user data.
     *
     * - Request: UpdateUserRequest
     * - Response: GetUserResponse
     */
    async update(userId: string, request: UpdateUserRequest): Promise<GetUserResponse> {
        return await this.put<GetUserResponse, UpdateUserRequest>(`/users/${userId}`, request);
    }

    /**
     * Update current authenticated user (me).
     *
     * - Request: UpdateMeRequest
     * - Response: GetUserResponse
     */
    async updateMe(request: UpdateMeRequest): Promise<GetUserResponse> {
        return await this.patch<GetUserResponse, UpdateMeRequest>("/users/me", request, { silentError: true });
    }

    /**
     * Change password for current authenticated user.
     *
     * - Request: ChangePasswordRequest
     * - Response: void
     */
    async changePassword(request: ChangePasswordRequest): Promise<void> {
        return await this.post<void, ChangePasswordRequest>("/users/me/change-password", request);
    }
}
export const usersApi = new UsersApi();
